import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DollarSign,
  FileText,
  Download,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
  } catch {
    return dateStr;
  }
};

// v3.14-151: Settlement Status Badge - Paid vs Due
const SettlementStatusBadge = ({ status }) => {
  if (status === 'paid') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="h-3 w-3" />
        Paid
      </span>
    );
  }
  // due (anything outstanding)
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
      <Clock className="h-3 w-3" />
      Due
    </span>
  );
};

// v3.14-150: Individual Payment Status Badge (computed dynamically)
// Computes status per-deal: Pending (0 paid), Partial (< owed), Paid (>= owed)
const PaymentStatusBadge = ({ owed, paid, label }) => {
  const paidAmount = paid || 0;
  const owedAmount = owed || 0;
  
  // No amount owed = not applicable
  if (owedAmount <= 0) {
    return (
      <span className="text-xs text-slate-400">N/A</span>
    );
  }
  
  // Paid >= Owed = Paid
  if (paidAmount >= owedAmount) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
        <CheckCircle className="h-2.5 w-2.5" />
        Paid
      </span>
    );
  }
  
  // Partial payment (> 0 but < owed)
  if (paidAmount > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
        <Clock className="h-2.5 w-2.5" />
        Partial
      </span>
    );
  }
  
  // No payment = Pending
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
      <Clock className="h-2.5 w-2.5" />
      Pending
    </span>
  );
};

const SettlementReportPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [filters, setFilters] = useState({
    payment_status: 'all',
    company_id: 'all',
    start_date: '',
    end_date: '',
  });
  
  // Mark as Paid dialog
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    record: null,
    type: null, // 'ptc' or 'partner'
  });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [submittingPayment, setSubmittingPayment] = useState(false);

  // Check if user is PTC internal
  const isPtcUser = user?.role?.startsWith('ptc_');

  // Fetch companies list for PTC users
  useEffect(() => {
    if (isPtcUser) {
      fetchCompanies();
    }
  }, [isPtcUser]);

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await api.get('/companies');
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.payment_status && filters.payment_status !== 'all') {
        params.payment_status = filters.payment_status;
      }
      if (filters.company_id && filters.company_id !== 'all') {
        params.company_id = filters.company_id;
      }
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await api.post('/reports/settlement', params);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to fetch settlement report:', error);
      toast.error('Failed to fetch settlement report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const handleResetFilters = () => {
    setFilters({
      payment_status: 'all',
      company_id: 'all',
      start_date: '',
      end_date: '',
    });
  };

  // Mark as Paid - Opens dialog to confirm zeroing out balance
  const openMarkAsPaidDialog = (record, type) => {
    // Calculate the amount to zero out
    const amount = type === 'ptc' ? record.ptc_fee_balance : record.partner_balance;
    setPaymentDialog({ open: true, record, type });
    setPaymentAmount(amount > 0 ? amount.toFixed(2) : '0');
  };

  const handleMarkAsPaid = async () => {
    if (!paymentDialog.record || !paymentDialog.type) return;
    
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmittingPayment(true);
    try {
      const paymentType = paymentDialog.type === 'ptc' ? 'paid_to_ptc' : 'paid_to_partner';
      await api.post('/reports/settlement/record-payment', {
        client_id: paymentDialog.record.client_id,
        payment_type: paymentType,
        amount: amount,
      });
      
      toast.success(`Marked as paid: ${formatCurrency(amount)}`);
      setPaymentDialog({ open: false, record: null, type: null });
      setPaymentAmount('');
      fetchReport();
    } catch (error) {
      console.error('Failed to record payment:', error);
      toast.error('Failed to record payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleExport = async (format) => {
    toast.info(`Exporting as ${format.toUpperCase()}...`);
    // Export logic here
  };

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settlement-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-primary">Settlement Ledger</h1>
          <p className="text-xs sm:text-sm text-slate-500">Track PTC fees and partner payments</p>
        </div>
        <div className="flex items-center gap-2">
          {report && (
            <>
              <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
                <Download className="h-4 w-4 mr-1" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('xlsx')}>
                <FileText className="h-4 w-4 mr-1" />
                Excel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters - With Company Filter for PTC Users */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            {/* Company Filter - Only for PTC Users */}
            {isPtcUser && (
              <div>
                <Label>Company</Label>
                <Select value={filters.company_id} onValueChange={(v) => setFilters({ ...filters, company_id: v })}>
                  <SelectTrigger data-testid="filter-company">
                    <SelectValue placeholder="All Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Settlement Status</Label>
              <Select value={filters.payment_status} onValueChange={(v) => setFilters({ ...filters, payment_status: v })}>
                <SelectTrigger data-testid="filter-payment-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="due">Due</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                data-testid="filter-end-date"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={fetchReport} className="bg-accent hover:bg-gold-dark" data-testid="generate-report-btn">
                <RefreshCw className="h-4 w-4 mr-1" />
                Generate Report
              </Button>
              <Button onClick={handleResetFilters} variant="outline" size="sm" data-testid="reset-filters-btn">
                Reset Filters
              </Button>
              <Button 
                onClick={async () => {
                  if (!window.confirm('Mark ALL funded deals as fully settled? This sets PTC Paid = PTC Owed and Partner Paid = Partner Owed for every funded deal.')) return;
                  try {
                    const res = await api.post('/admin/settle-all');
                    toast.success(`${res.data.settled_count} deals marked as settled`);
                    fetchReport();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed');
                  }
                }}
                variant="outline" 
                size="sm" 
                className="border-green-300 text-green-700 hover:bg-green-50"
                data-testid="settle-all-btn"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Mark All Settled
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {report && (
        <div className="space-y-4" data-testid="settlement-results">
          {/* v3.14-150: Summary Cards - Shows Outstanding (net) balances */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-[10px] font-medium text-blue-600 uppercase">Total Volume</p>
                    <p className="text-base font-bold text-blue-700">{formatCurrency(report.summary?.total_funded_volume)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="text-[10px] font-medium text-amber-600 uppercase">Partner Outstanding</p>
                    <p className="text-base font-bold text-amber-700">{formatCurrency(report.summary?.owed_to_partner)}</p>
                    <p className="text-[9px] text-amber-500">Net remaining to pay</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-[10px] font-medium text-green-600 uppercase">PTC Outstanding</p>
                    <p className="text-base font-bold text-green-700">{formatCurrency(report.summary?.owed_to_ptc)}</p>
                    <p className="text-[9px] text-green-500">Net remaining to collect</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-slate-600" />
                  <div>
                    <p className="text-[10px] font-medium text-slate-600 uppercase">Pending ACH</p>
                    <p className="text-base font-bold text-slate-700">{formatCurrency(report.summary?.pending_ach)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* v3.14-150: Records Table - Enhanced columns for Settlement/Payout Accuracy */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Settlement Ledger</CardTitle>
                <span className="text-sm text-slate-500">{report.records?.length || 0} records</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Guest Name</TableHead>
                      {isPtcUser && <TableHead className="font-semibold">Company</TableHead>}
                      <TableHead className="font-semibold">Deal Amount</TableHead>
                      <TableHead className="font-semibold">Funded</TableHead>
                      {/* v3.14-150: PTC Section */}
                      <TableHead className="font-semibold text-purple-700 bg-purple-50/50">PTC Owed</TableHead>
                      <TableHead className="font-semibold text-purple-700 bg-purple-50/50">PTC Paid</TableHead>
                      <TableHead className="font-semibold text-purple-700 bg-purple-50/50">PTC Outstanding</TableHead>
                      <TableHead className="font-semibold text-purple-700 bg-purple-50/50">PTC Status</TableHead>
                      {/* v3.14-150: Partner Section */}
                      <TableHead className="font-semibold text-blue-700 bg-blue-50/50">Partner Owed</TableHead>
                      <TableHead className="font-semibold text-blue-700 bg-blue-50/50">Partner Paid</TableHead>
                      <TableHead className="font-semibold text-blue-700 bg-blue-50/50">Partner Outstanding</TableHead>
                      <TableHead className="font-semibold text-blue-700 bg-blue-50/50">Partner Status</TableHead>
                      {/* v3.14-151: Funds Direction */}
                      <TableHead className="font-semibold">Funds Flow</TableHead>
                      {/* Overall Status */}
                      <TableHead className="font-semibold">Status</TableHead>
                      {user?.role === 'ptc_admin' && (
                        <TableHead className="font-semibold">Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={user?.role === 'ptc_admin' ? (isPtcUser ? 16 : 15) : (isPtcUser ? 15 : 14)} className="text-center py-8 text-slate-500">
                          No records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      report.records.map((record) => (
                        <TableRow 
                          key={record.id} 
                          className="hover:bg-slate-50 cursor-pointer transition-colors" 
                          data-testid={`settlement-row-${record.id}`}
                          onClick={() => {
                            if (!record.client_id) {
                              toast.error('Record is missing client_id - cannot navigate');
                              return;
                            }
                            navigate(`/clients/${record.client_id}`);
                          }}
                        >
                          <TableCell className="whitespace-nowrap text-sm">{formatDate(record.date)}</TableCell>
                          <TableCell 
                            className="font-medium text-sm text-blue-600 hover:text-blue-800 hover:underline"
                            data-testid={`settlement-guest-name-${record.id}`}
                          >
                            {record.guest_name}
                          </TableCell>
                          {isPtcUser && <TableCell className="text-sm text-slate-600">{record.company_name || '-'}</TableCell>}
                          <TableCell className="text-sm">{formatCurrency(record.deal_amount)}</TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-0.5 rounded ${record.funded === 'Yes' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                              {record.funded}
                            </span>
                          </TableCell>
                          {/* v3.14-150: PTC Columns */}
                          <TableCell className="text-sm font-medium text-purple-700 bg-purple-50/30">{formatCurrency(record.ptc_fee)}</TableCell>
                          <TableCell className="text-sm text-purple-600 bg-purple-50/30">{formatCurrency(record.amount_paid_to_ptc)}</TableCell>
                          <TableCell className="text-sm font-semibold text-purple-800 bg-purple-50/30">
                            {record.ptc_fee_balance > 0 ? formatCurrency(record.ptc_fee_balance) : <span className="text-green-600">$0.00</span>}
                          </TableCell>
                          <TableCell className="bg-purple-50/30">
                            <PaymentStatusBadge owed={record.ptc_fee} paid={record.amount_paid_to_ptc} />
                          </TableCell>
                          {/* v3.14-150: Partner Columns */}
                          <TableCell className="text-sm font-medium text-blue-700 bg-blue-50/30">{formatCurrency(record.net_to_partner)}</TableCell>
                          <TableCell className="text-sm text-blue-600 bg-blue-50/30">{formatCurrency(record.amount_paid_to_partner)}</TableCell>
                          <TableCell className="text-sm font-semibold text-blue-800 bg-blue-50/30">
                            {record.partner_balance > 0 ? formatCurrency(record.partner_balance) : <span className="text-green-600">$0.00</span>}
                          </TableCell>
                          <TableCell className="bg-blue-50/30">
                            <PaymentStatusBadge owed={record.net_to_partner} paid={record.amount_paid_to_partner} />
                          </TableCell>
                          {/* v3.14-151: Funds Direction */}
                          <TableCell className="text-xs">
                            {record.funded === 'Yes' ? (
                              record.settlement_status === 'paid' ? (
                                <span className="px-2 py-1 rounded bg-green-100 text-green-700 font-medium">Complete</span>
                              ) : (
                                <span className={`px-2 py-1 rounded ${record.ach_entered === 'Yes' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                  {record.funds_direction || (record.ach_entered === 'Yes' ? 'PTC → Partner' : 'Partner → PTC')}
                                </span>
                              )
                            ) : '-'}
                          </TableCell>
                          {/* Overall Status */}
                          <TableCell><SettlementStatusBadge status={record.settlement_status} /></TableCell>
                          {user?.role === 'ptc_admin' && (
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <div className="flex gap-1">
                                {/* Mark PTC as Paid button */}
                                {record.ptc_fee_balance > 0 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs h-7 px-2 text-purple-700 border-purple-300 hover:bg-purple-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openMarkAsPaidDialog(record, 'ptc');
                                    }}
                                    data-testid={`mark-ptc-paid-${record.id}`}
                                  >
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    PTC Paid
                                  </Button>
                                )}
                                {/* Mark Partner as Paid button */}
                                {record.partner_balance > 0 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs h-7 px-2 text-blue-700 border-blue-300 hover:bg-blue-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openMarkAsPaidDialog(record, 'partner');
                                    }}
                                    data-testid={`mark-partner-paid-${record.id}`}
                                  >
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Partner Paid
                                  </Button>
                                )}
                                {record.ptc_fee_balance <= 0 && record.partner_balance <= 0 && record.funded === 'Yes' && (
                                  <span className="text-xs text-green-600 flex items-center">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    All Paid
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mark as Paid Dialog */}
      <Dialog open={paymentDialog.open} onOpenChange={(open) => !open && setPaymentDialog({ open: false, record: null, type: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Mark as Paid - {paymentDialog.type === 'ptc' ? 'PTC Fee' : 'Partner Payment'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {paymentDialog.record && (
              <>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm font-medium">{paymentDialog.record.guest_name}</p>
                  <p className="text-xs text-slate-500">
                    Outstanding Balance: {formatCurrency(paymentDialog.type === 'ptc' ? paymentDialog.record.ptc_fee_balance : paymentDialog.record.partner_balance)}
                  </p>
                </div>
                <div>
                  <Label>Payment Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="payment-amount-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Enter the full balance amount to mark as fully paid.
                  </p>
                </div>
                <Button
                  className="w-full bg-accent hover:bg-gold-dark"
                  onClick={handleMarkAsPaid}
                  disabled={submittingPayment || !paymentAmount}
                  data-testid="confirm-payment-btn"
                >
                  {submittingPayment ? 'Processing...' : `Mark ${formatCurrency(parseFloat(paymentAmount) || 0)} as Paid`}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettlementReportPage;
